import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import type { Props as HorizontalProductBoxMolProps } from "@onestore/hel/dist/components/molecules/HorizontalProductBoxMol"
import HorizontalProductBoxMol from "@onestore/hel/dist/components/molecules/HorizontalProductBoxMol"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import { formatted } from "@core/pricing"
import { PriceType } from "@core/types"
import type { HorizontalProductBox } from "@gatsby-plugin-generic-page/fragments/horizontalProductBox"
import type { TableHeader } from "@gatsby-plugin-generic-page/fragments/tableHeader"
import get from "lodash/get"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { getTableAriaLabels } from "~/helpers/getTableAriaLabels"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import isEmpty from "~/lib/isEmpty"
import { useConfigsHookContext } from "../.."
import { useHorizontalTableCells } from "./cells/useHorizontalTableCells"

export type Props = {
  header: TableHeader
  productData: HorizontalProductBox
}

export default function TableRow({
  header,
  productData,
}: Props): ReactElement<Props> {
  const { currentPriceType } = usePriceTypeContext()
  const { showDetailedPrices } = usePriceConfigurationQuery()

  const {
    configsValues,
    isCalculateProductsLoading,
    getHasProductActiveResourceCategories,
    getCalculateProductPrices,
    getUrlConfigsValues,
  } = useConfigsHookContext() ?? {}

  const cellsObject = useHorizontalTableCells(
    productData
  ) as HorizontalProductBoxMolProps["items"]

  if (isEmpty(cellsObject)) {
    return <></>
  }

  const { saleConfiguration, cells } = productData.flatData

  if (
    !getUrlConfigsValues ||
    !getHasProductActiveResourceCategories ||
    !getHasProductActiveResourceCategories(productData.id)
  ) {
    return <></>
  }

  const columnsCount = header.columns.length

  if (cells.length !== columnsCount || isEmpty(saleConfiguration)) {
    return <></>
  }

  const plan = get(saleConfiguration[0], "flatData.defaultCloudBluePlan[0]")

  if (isEmpty(plan)) {
    return <></>
  }

  const calculatePrices = getCalculateProductPrices
    ? getCalculateProductPrices(plan.flatData.id)
    : null

  const productPlanPeriods = plan.flatData.periods

  const selectedPeriodObject = configsValues
    ? productPlanPeriods.find(
        (period) => period.period_name === configsValues.period
      )
    : null

  const periodInfo = selectedPeriodObject
    ? new PeriodInfo(selectedPeriodObject)
    : undefined

  if (!periodInfo) {
    return <></>
  }

  const { rowLabel, cellLabel } = getTableAriaLabels()

  const { renewPrice } = periodInfo.getPrices(currentPriceType, true)

  const priceTypeText =
    currentPriceType === PriceType.NETTO
      ? _t("prices.netto")
      : _t("prices.gross")

  const renewSuffix = `${_t(
    `periodName.renewPrice.${periodInfo.getPeriodType()}`,
    {
      smart_count: periodInfo.getPeriodLength(),
    }
  )}`

  const updateRenewalPriceValue =
    !isEmpty(calculatePrices) && !isEmpty(calculatePrices.renewalPrice)
      ? formatted(calculatePrices.renewalPrice, currentPriceType).full
      : renewPrice

  const renewalPriceElement = isCalculateProductsLoading ? null : (
    <TextAtm typography="nano1" emphasis="medium">
      {`${_t("prices.renewal")}: ${updateRenewalPriceValue} ${priceTypeText} ${renewSuffix}`}
    </TextAtm>
  )

  return (
    <HorizontalProductBoxMol
      items={cellsObject}
      promotionText={productData.flatData.promoText}
      renewalPrice={showDetailedPrices ? undefined : renewalPriceElement}
      ariaLabels={{
        cellLabel,
        rowLabel,
      }}
    />
  )
}
